import axios from "axios";
import { auth } from "@/auth";
import store from "@/store/root";
import {
  JobStatusResponse,
  Page,
  SuccessfulResponse
} from "@/tools/msa/models/requests/extraction/modern";
import { DocumentsResponse } from "@/tools/msa/models/requests/splitter";

export const makeStatusRequestor = () => async (
  jobId: string,
  sessionId: string
): Promise<SuccessfulResponse> =>
  axios
    .post<JobStatusResponse>(
      "/results",
      { id: jobId },
      {
        baseURL: (store.state as any).settings.current.endpoints.textractor,
        headers: { "X-Session-Id": sessionId, ...(await auth.headers()) }
      }
    )
    .then(
      (response): Promise<SuccessfulResponse> =>
        response.data.state === "succeeded"
          ? Promise.resolve(response.data)
          : Promise.reject({ type: "application", response })
    )
    .catch(error => Promise.reject({ type: "other", ...error }));

export const makePageCollector = (
  requestor: (jobId: string, sessionId: string) => Promise<SuccessfulResponse>
) => {
  return function collector(jobId: string, sessionId: string): Promise<Page[]> {
    return requestor(jobId, sessionId).then(response =>
      (response.nextId
        ? collector(response.nextId, sessionId)
        : Promise.resolve([])
      ).then(pages => [...response.pages, ...pages])
    );
  };
};

export const makePagesSplitter = () => async (
  pages: Page[],
  sessionId: string
): Promise<Page[][]> =>
  axios
    .post<DocumentsResponse>(
      "/documents",
      { pages },
      {
        baseURL: (store.state as any).settings.current.endpoints.textractor,
        headers: { "X-Session-Id": sessionId, ...(await auth.headers()) }
      }
    )
    .then(response => response.data.documents);

export const processResults = async (context: any, event: any) => {
  const splitter = makePagesSplitter();
  const collector = makePageCollector(makeStatusRequestor());

  return collector(context.jobId, context.sessionId)
    .then(pages => splitter(pages, context.sessionId))
    .then(documents => ({
      outcome: "success",
      documents
    }))
    .catch(error =>
      error.type === "application"
        ? {
            outcome: "error",
            type: "invalid_status",
            status:
              error.response.data.state === "in_progress"
                ? "IN_PROGRESS"
                : error.response.data.error.status,
            message:
              error.response.data.state === "in_progress"
                ? ""
                : error.response.data.error.message
          }
        : Promise.reject(error)
    );
};
